'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import { ApiInitiative } from '@/lib/api';
import { LazyImage } from '../LazyImage';
import { formatEventDateRange, INITIATIVE_OCCASION_OPTIONS } from '@sizzlenz/shared';
import { Calendar, Copy, MapPin, BarChart3, Edit } from 'lucide-react';
import { InitiativeParticipationAction } from './InitiativeParticipationAction';
import { getInitiativeEditUrl } from '@/lib/utils/initiative-urls';
import { Markdown } from '../Markdown';
import { ImageGallery, type ImageGalleryDisplayMode } from '@/components/ui/image-gallery';
import { EmbeddedMap } from '@/components/ui/embedded-map';
import { ActivationSettingsSnapshot } from './ActivationSettingsSnapshot';
import { ContactInfo } from '@/components/ui/contact-info';
import { useSession } from '@/lib/auth-client';
import { EnquiryForm, type EnquiryTypeOption } from '@/components/enquiries';
import { canAccessInitiativeContact } from '@/lib/utils/trade';
import { FollowButton } from '../FollowButton';
import { ActivityOperations, CommentSection } from '@/components/activities';
import { InterestedButton, AttendButton } from '@/components/activities';
import { useInitiativePermissions } from '@/lib/hooks/useInitiativePermissions';
import { InitiativeAnalytics } from './InitiativeAnalytics';
import { ObjectComponentsSummary } from '@/components/object-components/ObjectComponentsSummary';
import { DemographicsSummary } from './DemographicsSummary';
import { InitiativeProposalSnapshot } from './InitiativeProposalSnapshot';
import { RegistrationBlock } from './RegistrationBlock';
import type { ObjectComponent } from '@sizzlenz/shared';
import type { InitiativeDemographics, AttendanceSettings } from '@sizzlenz/shared';
import { NewInitiativeDialog } from './NewInitiativeDialog';
import { Button } from '../ui/button';
import { AccessGrantLinkButton } from '@/components/access-grants';
import { AssignmentTableBulkActions } from './AssignmentTableBulkActions';
import { LinkedHubs } from '@/components/LinkedHubs';
import { RelatedContent } from '@/components/content/RelatedContent';

// ─── Types ───────────────────────────────────────────────────────────────────

interface InitiativeDetailProps {
  initiative: ApiInitiative;
  onParticipationSuccess?: () => void;
  showParticipationAction?: boolean;
  showActivationSettings?: boolean;
  galleryDisplayMode?: ImageGalleryDisplayMode;
  defaultEnquiryType?: string;
  /** When true, the viewer accessed this initiative via an access grant link */
  accessGranted?: boolean;
}
interface AddressDetail {
  formatted_address?: string;
  display_address?: string;
  url?: string;
  geometry?: {
    location?: {
      lat: number;
      lng: number;
    };
  };
}

// ─── Section Components ──────────────────────────────────────────────────────

function ActionButtons(t0) {
  const $ = _c(16);
  const {
    initiative,
    isAdmin,
    canEdit,
    editUrl,
    onClone
  } = t0;
  let t1;
  if ($[0] !== initiative.following || $[1] !== initiative.id) {
    t1 = initiative.following !== undefined && <FollowButton objectId={initiative.id} objectType="initiative" following={initiative.following ?? false} showLabel={true} />;
    $[0] = initiative.following;
    $[1] = initiative.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isAdmin || $[4] !== onClone) {
    t2 = isAdmin && <button onClick={onClone} className="p-1.5 md:p-2 bg-white/90 hover:bg-white border border-gray-200 rounded-md md:rounded-lg transition-colors cursor-pointer text-gray-700 shadow-sm backdrop-blur-sm" title="Clone Initiative"><Copy className="w-4 h-4 md:w-5 md:h-5" /></button>;
    $[3] = isAdmin;
    $[4] = onClone;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== canEdit || $[7] !== initiative) {
    t3 = canEdit && initiative.ownerType === "entity" && initiative.ownerId && <AccessGrantLinkButton variant="outline" size="sm" className="gap-1 cursor-pointer bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm text-xs md:text-sm md:gap-2" title="Generate access link from an existing grant" dialog={{
      mode: "pick-grant",
      initiative,
      grantPickerEntityScopeId: initiative.ownerId
    }}><span className="hidden md:inline">Access link</span><span className="md:hidden">Link</span></AccessGrantLinkButton>;
    $[6] = canEdit;
    $[7] = initiative;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== editUrl) {
    t4 = editUrl && <Link href={editUrl} title="Edit Initiative"><Button variant="outline" size="sm" className="gap-1 cursor-pointer bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm text-xs md:text-sm md:gap-2"><Edit className="h-3.5 w-3.5 md:h-4 md:w-4" /><span className="hidden md:inline">Edit</span></Button></Link>;
    $[9] = editUrl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <div className="absolute top-2 right-2 md:top-4 md:right-4 z-10 flex items-center gap-1 md:gap-2">{t1}{t2}{t3}{t4}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function HeroImage(t0) {
  const $ = _c(31);
  const {
    initiative,
    imageUrl,
    startDate,
    endDate,
    isActiveNow
  } = t0;
  const t1 = imageUrl || "";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== initiative.name || $[2] !== t1) {
    t3 = <LazyImage src={t1} alt={initiative.name} className="w-full h-full object-cover" fallback={t2} />;
    $[1] = initiative.name;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== initiative.type) {
    t5 = <span className="inline-block px-2 py-0.5 mb-2 text-xs font-semibold tracking-wider text-indigo-900 bg-indigo-100 rounded-full uppercase">{initiative.type}</span>;
    $[5] = initiative.type;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== initiative.status) {
    t6 = initiative.status === "proposal" && <span className="inline-block px-2 py-0.5 mb-2 ml-2 text-xs font-semibold tracking-wider text-blue-900 bg-blue-100 rounded-full uppercase">Proposal</span>;
    $[7] = initiative.status;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== initiative.status) {
    t7 = initiative.status === "gauging" && <span className="inline-block px-2 py-0.5 mb-2 ml-2 text-xs font-semibold tracking-wider text-blue-900 bg-blue-100 rounded-full uppercase">Gauging Interest</span>;
    $[9] = initiative.status;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== initiative.isTemplate) {
    t8 = initiative.isTemplate && <span className="inline-block px-2 py-0.5 mb-2 ml-2 text-xs font-semibold tracking-wider text-amber-900 bg-amber-100 rounded-full uppercase">Template</span>;
    $[11] = initiative.isTemplate;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== initiative.name) {
    t9 = <h1 className="text-2xl md:text-4xl font-bold">{initiative.name}</h1>;
    $[13] = initiative.name;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== endDate || $[16] !== startDate) {
    t10 = (startDate || endDate) && <span className="flex items-center gap-1.5 text-sm text-white/90"><Calendar className="w-3.5 h-3.5" />{formatEventDateRange(startDate, endDate)}</span>;
    $[15] = endDate;
    $[16] = startDate;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isActiveNow) {
    t11 = isActiveNow && <div className="flex items-center gap-2 text-sm text-emerald-400 font-medium"><span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" /></span>Active Now</div>;
    $[18] = isActiveNow;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t12 = <div className="absolute bottom-4 left-4 right-4 text-white md:bottom-6 md:left-6 md:right-6">{t5}{t6}{t7}{t8}{t9}{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t3) {
    t13 = <div className="relative h-[200px] md:h-[360px] w-full bg-gray-100">{t3}{t4}{t12}</div>;
    $[28] = t12;
    $[29] = t3;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}
function OwnerAndReactions(t0) {
  const $ = _c(22);
  const {
    initiative,
    allowReactions,
    votingEnabled,
    interestMode,
    structuredInterestConfig,
    interestDisabled
  } = t0;
  let t1;
  if ($[0] !== initiative.data || $[1] !== initiative.owner) {
    t1 = initiative.owner && !(initiative.data as any)?.anonymous?.enabled && <Link href={initiative.owner.objectType === "entity" ? `/entities/${initiative.owner.objectId}` : `/users/${initiative.owner.objectId}`} className="font-medium text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer">{initiative.owner.name}</Link>;
    $[0] = initiative.data;
    $[1] = initiative.owner;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="flex items-center gap-3 text-sm text-gray-600">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== initiative.engagement?.votes?.interested?.count || $[6] !== initiative.id || $[7] !== interestDisabled || $[8] !== interestMode || $[9] !== structuredInterestConfig) {
    t3 = interestMode !== "disabled" && <InterestedButton objectId={String(initiative.id)} objectType="initiative" interestedCount={initiative.engagement?.votes?.interested?.count} countInline={true} interestMode={interestMode} structuredInterestConfig={structuredInterestConfig} disabled={interestDisabled} />;
    $[5] = initiative.engagement?.votes?.interested?.count;
    $[6] = initiative.id;
    $[7] = interestDisabled;
    $[8] = interestMode;
    $[9] = structuredInterestConfig;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = String(initiative.id);
  let t5;
  if ($[11] !== allowReactions || $[12] !== initiative.name || $[13] !== t4 || $[14] !== votingEnabled) {
    t5 = <ActivityOperations dialogEnabled={true} showVotes={votingEnabled} showReactions={allowReactions} objectId={t4} objectType="initiative" title={initiative.name} />;
    $[11] = allowReactions;
    $[12] = initiative.name;
    $[13] = t4;
    $[14] = votingEnabled;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t3 || $[17] !== t5) {
    t6 = <div className="flex flex-wrap items-center gap-3">{t3}{t5}</div>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t2 || $[20] !== t6) {
    t7 = <div className="space-y-3"><div className="flex flex-wrap items-center justify-between gap-3">{t2}{t6}</div></div>;
    $[19] = t2;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function ProposalSection(t0) {
  const $ = _c(3);
  const {
    proposalStatus,
    onProvideFeedback
  } = t0;
  let t1;
  if ($[0] !== onProvideFeedback || $[1] !== proposalStatus) {
    t1 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100"><InitiativeProposalSnapshot proposalStatus={proposalStatus} onProvideFeedback={onProvideFeedback} /></div>;
    $[0] = onProvideFeedback;
    $[1] = proposalStatus;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function OccasionsSection(t0) {
  const $ = _c(5);
  const {
    occasions
  } = t0;
  if (occasions.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Occasion</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== occasions) {
    t2 = occasions.map(_temp);
    $[1] = occasions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">{t1}<div className="flex flex-wrap gap-1.5">{t2}</div></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp(occasion) {
  const label = INITIATIVE_OCCASION_OPTIONS.find(o => o.key === occasion)?.label || occasion;
  return <span key={occasion} className="bg-white border border-gray-200 text-gray-600 text-[11px] px-2.5 py-1 rounded-md">{label}</span>;
}
function DemographicsSection(t0) {
  const $ = _c(2);
  const {
    demographics
  } = t0;
  let t1;
  if ($[0] !== demographics) {
    t1 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100"><DemographicsSummary demographics={demographics} /></div>;
    $[0] = demographics;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ActivationDetailsSection(t0) {
  const $ = _c(3);
  const {
    settings
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Details</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== settings) {
    t2 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">{t1}<ActivationSettingsSnapshot settings={settings} /></div>;
    $[1] = settings;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function TagsSection(t0) {
  const $ = _c(4);
  const {
    tags
  } = t0;
  if (tags.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== tags) {
    t1 = tags.map(_temp2);
    $[0] = tags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="bg-gray-50 p-4 rounded-xl border border-gray-100"><div className="flex flex-wrap gap-1.5">{t1}</div></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(tag) {
  return <span key={tag} className="bg-white border border-gray-200 text-gray-600 text-[11px] px-2.5 py-1 rounded-md">{tag}</span>;
}
function ComponentsSection(t0) {
  const $ = _c(5);
  const {
    components,
    canEdit
  } = t0;
  if (components.length === 0) {
    return null;
  }
  const filter = canEdit ? "all" as const : "open-provider" as const;
  const title = canEdit ? "Providers Wanted" : "Providers wanted - can you help?";
  const t1 = !canEdit;
  let t2;
  if ($[0] !== components || $[1] !== filter || $[2] !== t1 || $[3] !== title) {
    t2 = <ObjectComponentsSummary components={components} filter={filter} title={title} expanded={true} publicOnly={t1} />;
    $[0] = components;
    $[1] = filter;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function LocationSection(t0) {
  const $ = _c(12);
  const {
    addressDetail,
    displayAddress,
    hasMapCoords,
    shouldShowAddress,
    shouldShowMap
  } = t0;
  const showAddress = addressDetail?.formatted_address && shouldShowAddress;
  const showMap = hasMapCoords && shouldShowMap;
  if (!showAddress && !showMap) {
    return null;
  }
  let t1;
  if ($[0] !== addressDetail || $[1] !== displayAddress || $[2] !== shouldShowAddress) {
    t1 = displayAddress && shouldShowAddress && <div className="text-sm font-semibold text-gray-500 flex items-center gap-1.5"><MapPin className="w-4 h-4 flex-shrink-0" />{addressDetail?.url ? <a href={addressDetail.url} target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">{displayAddress}</a> : <span>{displayAddress}</span>}</div>;
    $[0] = addressDetail;
    $[1] = displayAddress;
    $[2] = shouldShowAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== addressDetail || $[5] !== displayAddress || $[6] !== hasMapCoords || $[7] !== shouldShowMap) {
    t2 = hasMapCoords && shouldShowMap && <EmbeddedMap lat={addressDetail.geometry.location.lat} lng={addressDetail.geometry.location.lng} formattedAddress={displayAddress && !addressDetail?.url ? displayAddress : undefined} />;
    $[4] = addressDetail;
    $[5] = displayAddress;
    $[6] = hasMapCoords;
    $[7] = shouldShowMap;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <section className="space-y-2">{t1}{t2}</section>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function ContactSection(t0) {
  const $ = _c(2);
  const {
    contactDetails
  } = t0;
  let t1;
  if ($[0] !== contactDetails) {
    t1 = <section className="bg-white rounded-lg shadow p-4"><ContactInfo contactDetails={contactDetails} /></section>;
    $[0] = contactDetails;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RelatedContentSection(t0) {
  const $ = _c(14);
  const {
    objectType,
    objectId,
    tags,
    title,
    blurb
  } = t0;
  const t1 = title || "Related Content";
  let t2;
  if ($[0] !== t1) {
    t2 = <h3 className="text-lg font-semibold text-gray-900">{t1}</h3>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== blurb) {
    t3 = blurb && <p className="text-sm text-gray-600 mt-1">{blurb}</p>;
    $[2] = blurb;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== objectId || $[8] !== objectType || $[9] !== tags) {
    t5 = <RelatedContent objectType={objectType} objectId={objectId} tags={tags} title={null} limit={6} />;
    $[7] = objectId;
    $[8] = objectType;
    $[9] = tags;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <section className="p-4 md:p-6 border-t border-gray-100 space-y-3">{t4}{t5}</section>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
const EnquirySection = React.forwardRef<HTMLDivElement, {
  initiativeId: number;
  enquiryTypes: EnquiryTypeOption[];
  defaultEnquiryType?: string;
}>(function EnquirySection(t0, ref) {
  const $ = _c(7);
  const {
    initiativeId,
    enquiryTypes,
    defaultEnquiryType
  } = t0;
  let t1;
  if ($[0] !== defaultEnquiryType || $[1] !== enquiryTypes || $[2] !== initiativeId) {
    t1 = <EnquiryForm apiRoute="initiatives/enquiries" resourceType="initiative" resourceId={initiativeId} enquiryTypes={enquiryTypes} className="p-2" showType={true} context="initiative-detail" showSubject={false} sessionRequiredToSubmit={true} modalSignup={true} defaultEnquiryType={defaultEnquiryType} onSuccess={_temp3} />;
    $[0] = defaultEnquiryType;
    $[1] = enquiryTypes;
    $[2] = initiativeId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== ref || $[5] !== t1) {
    t2 = <div ref={ref}>{t1}</div>;
    $[4] = ref;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});
function GallerySection(t0) {
  const $ = _c(3);
  const {
    galleryItems,
    displayMode
  } = t0;
  if (galleryItems.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== displayMode || $[1] !== galleryItems) {
    t1 = <div className="p-4 md:p-6 border-t border-gray-100"><ImageGallery items={galleryItems} displayMode={displayMode} className="rounded-xl overflow-hidden" /></div>;
    $[0] = displayMode;
    $[1] = galleryItems;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function AnalyticsSection(t0) {
  const $ = _c(8);
  const {
    initiative
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-gray-500" />Analytics</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== initiative) {
    t2 = <div className="max-w-md"><InitiativeAnalytics initiative={initiative} /></div>;
    $[1] = initiative;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `/initiatives/${initiative.id}/activity`;
  let t4;
  if ($[3] !== t3) {
    t4 = <div className="mt-4"><Link href={t3} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer">{"View Engagement & Activity \u2192"}</Link></div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="p-4 md:p-6 border-t border-gray-100">{t1}{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function InitiativeDetail(t0) {
  const $ = _c(156);
  const {
    initiative,
    onParticipationSuccess,
    showParticipationAction: t1,
    showActivationSettings: t2,
    galleryDisplayMode: t3,
    defaultEnquiryType,
    accessGranted: t4
  } = t0;
  const showParticipationAction = t1 === undefined ? false : t1;
  const showActivationSettings = t2 === undefined ? true : t2;
  const galleryDisplayMode = t3 === undefined ? "grid" : t3;
  const accessGranted = t4 === undefined ? false : t4;
  const imageUrl = (initiative as any).image;
  const startDate = initiative.startDate;
  const endDate = initiative.endDate;
  let t5;
  if ($[0] !== initiative.data) {
    t5 = (initiative.data as any)?.occasions || [];
    $[0] = initiative.data;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const occasions = t5;
  const addressDetail = (initiative.data as any)?.address_1 as AddressDetail | null | undefined;
  const displayAddress = addressDetail?.display_address || addressDetail?.formatted_address;
  const hasMapCoords = addressDetail?.geometry?.location?.lat != null && addressDetail?.geometry?.location?.lng != null;
  const geoSettings = (initiative.data as any)?.geo;
  const shouldShowAddress = geoSettings?.showAddress ?? true;
  const shouldShowMap = geoSettings?.showMap ?? true;
  let t6;
  if ($[2] !== initiative.data) {
    t6 = (initiative.data as any)?.gallery?.images as string[] || [];
    $[2] = initiative.data;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const galleryImages = t6;
  let t7;
  if ($[4] !== initiative.data) {
    t7 = (initiative.data as any)?.contact || {};
    $[4] = initiative.data;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const contactDetails = t7;
  const {
    data: session
  } = useSession();
  const {
    hasPermission,
    isPlatformAdmin
  } = useInitiativePermissions(initiative.id);
  let t8;
  if ($[6] !== hasPermission) {
    t8 = hasPermission("edit");
    $[6] = hasPermission;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const canEdit = t8;
  let t9;
  if ($[8] !== hasPermission) {
    t9 = hasPermission("view_analytics");
    $[8] = hasPermission;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const canViewAnalytics = t9;
  let t10;
  if ($[10] !== hasPermission || $[11] !== isPlatformAdmin) {
    t10 = isPlatformAdmin || hasPermission("manage_members");
    $[10] = hasPermission;
    $[11] = isPlatformAdmin;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const canManageMembers = t10;
  const isAdmin = session?.user?.isAdmin === true;
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [enquiryTypeOverride, setEnquiryTypeOverride] = useState(undefined);
  const enquiryFormRef = useRef(null);
  let t11;
  if ($[13] !== initiative.data) {
    t11 = (initiative.data as any)?.components as ObjectComponent[] || [];
    $[13] = initiative.data;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const components = t11;
  const relatedContentConfig = (initiative.data as any)?.relatedContent as {
    enabled: boolean;
    tags?: string[];
    title?: string;
    blurb?: string;
  } | null | undefined;
  const demographics = (initiative.data as any)?.demographics as InitiativeDemographics | null | undefined;
  const showDemographicsSummary = !!demographics && (demographics.ageGroups?.length > 0 || demographics.gender || demographics.numberOfAttendees != null);
  const allowReactions = (initiative.data as any)?.allowReactions !== false;
  const allowComments = (initiative.data as any)?.allowComments !== false;
  const votingEnabled = (initiative.data as any)?.votingEnabled === true;
  const interestMode = (initiative.data as any)?.interestMode || ((initiative.data as any)?.votingMode === "interest" ? "simple" : "disabled");
  let t12;
  if ($[15] !== initiative.data) {
    t12 = (initiative.data as any)?.structuredInterestConfig || {
      enableEntitySelection: true,
      requireEntity: true,
      allowNote: true,
      requireNote: false,
      allowMultipleEntities: true
    };
    $[15] = initiative.data;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const structuredInterestConfig = t12;
  let t13;
  if ($[17] !== initiative.data) {
    t13 = (initiative.data as any)?.attendance || {
      enabled: false,
      requiresApproval: false,
      capacity: null
    };
    $[17] = initiative.data;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const attendanceSettings = t13;
  const hasContactEmail = !!contactDetails.email;
  const hideContactDetails = contactDetails.hideDetails === true;
  const contactVisibility = contactDetails.visibility;
  const t14 = session?.user?.isAdmin;
  const t15 = (session?.user?.data as any)?.featureFlags;
  let t16;
  if ($[19] !== contactVisibility || $[20] !== t14 || $[21] !== t15) {
    t16 = canAccessInitiativeContact(contactVisibility, {
      isAdmin: t14,
      featureFlags: t15
    });
    $[19] = contactVisibility;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const canAccessContact = t16;
  const shouldShowEnquiryForm = hasContactEmail && canAccessContact;
  const shouldShowContactDetails = hasContactEmail && !hideContactDetails && canAccessContact && (contactDetails.name || contactDetails.phone || contactDetails.website);
  let t17;
  if ($[23] !== galleryImages) {
    t17 = galleryImages.map(_temp4);
    $[23] = galleryImages;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const galleryItems = t17;
  let t18;
  if ($[25] !== canEdit || $[26] !== initiative.id || $[27] !== initiative.ownerId || $[28] !== initiative.ownerType) {
    t18 = canEdit && initiative.ownerType && initiative.ownerId ? getInitiativeEditUrl(initiative.ownerType, typeof initiative.ownerId === "string" ? parseInt(initiative.ownerId, 10) : initiative.ownerId, initiative.id) : null;
    $[25] = canEdit;
    $[26] = initiative.id;
    $[27] = initiative.ownerId;
    $[28] = initiative.ownerType;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const editUrl = t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [{
      value: "general_enquiry",
      label: "General Enquiry",
      enabled: true
    }, {
      value: "partnership",
      label: "Partnership Enquiry",
      enabled: true
    }, {
      value: "participation",
      label: "Participation Enquiry",
      enabled: true
    }, {
      value: "provider",
      label: "Provider Enquiry",
      enabled: true
    }, {
      value: "proposal_feedback",
      label: "Proposal Feedback",
      enabled: true
    }, {
      value: "custom",
      label: "Custom",
      enabled: false
    }];
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const enquiryTypes = t19;
  const showProposal = initiative.status === "proposal" && (canEdit || accessGranted);
  const showActivation = showActivationSettings && initiative.type === "activation" && (initiative.data as any)?.activation?.roles?.length > 0;
  const showEnquiry = shouldShowEnquiryForm || showProposal;
  const activeEnquiryType = enquiryTypeOverride ?? defaultEnquiryType;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setEnquiryTypeOverride("proposal_feedback");
      setTimeout(() => {
        enquiryFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }, 50);
    };
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const handleProvideFeedback = t20;
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsCloneDialogOpen(true);
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== canEdit || $[34] !== editUrl || $[35] !== initiative || $[36] !== isAdmin) {
    t22 = <ActionButtons initiative={initiative} isAdmin={isAdmin} canEdit={canEdit} editUrl={editUrl} onClone={t21} />;
    $[33] = canEdit;
    $[34] = editUrl;
    $[35] = initiative;
    $[36] = isAdmin;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== endDate || $[39] !== imageUrl || $[40] !== initiative || $[41] !== startDate) {
    t23 = <HeroImage initiative={initiative} imageUrl={imageUrl} startDate={startDate} endDate={endDate} isActiveNow={false} />;
    $[38] = endDate;
    $[39] = imageUrl;
    $[40] = initiative;
    $[41] = startDate;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== allowReactions || $[44] !== attendanceSettings || $[45] !== initiative || $[46] !== interestMode || $[47] !== structuredInterestConfig || $[48] !== votingEnabled) {
    t24 = <OwnerAndReactions initiative={initiative} allowReactions={allowReactions} votingEnabled={votingEnabled} interestMode={interestMode} structuredInterestConfig={structuredInterestConfig} interestDisabled={false} attendanceSettings={attendanceSettings} />;
    $[43] = allowReactions;
    $[44] = attendanceSettings;
    $[45] = initiative;
    $[46] = interestMode;
    $[47] = structuredInterestConfig;
    $[48] = votingEnabled;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== initiative.proposalStatus || $[51] !== showProposal) {
    t25 = showProposal && <div className="lg:hidden"><ProposalSection proposalStatus={initiative.proposalStatus} onProvideFeedback={handleProvideFeedback} /></div>;
    $[50] = initiative.proposalStatus;
    $[51] = showProposal;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== attendanceSettings || $[54] !== initiative.engagement?.attendance?.registered?.count || $[55] !== initiative.id) {
    t26 = attendanceSettings.enabled && attendanceSettings.showRegistrationBlock !== false && (attendanceSettings.blockPosition === "main" || attendanceSettings.blockPosition === "both") && <RegistrationBlock objectId={String(initiative.id)} objectType="initiative" attendanceSettings={attendanceSettings} attendanceCount={initiative.engagement?.attendance?.registered?.count} />;
    $[53] = attendanceSettings;
    $[54] = initiative.engagement?.attendance?.registered?.count;
    $[55] = initiative.id;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== initiative.description) {
    t27 = initiative.description && <section><p className="text-gray-700 whitespace-pre-line">{initiative.description}</p></section>;
    $[57] = initiative.description;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== initiative.content) {
    t28 = initiative.content && <section className="prose prose-indigo max-w-full"><Markdown>{initiative.content}</Markdown></section>;
    $[59] = initiative.content;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t24 || $[62] !== t25 || $[63] !== t26 || $[64] !== t27 || $[65] !== t28) {
    t29 = <div className="space-y-10 order-1 min-w-0">{t24}{t25}{t26}{t27}{t28}</div>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== initiative.proposalStatus || $[68] !== showProposal) {
    t30 = showProposal && <div className="lg:block hidden"><ProposalSection proposalStatus={initiative.proposalStatus} onProvideFeedback={handleProvideFeedback} /></div>;
    $[67] = initiative.proposalStatus;
    $[68] = showProposal;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== initiative || $[71] !== onParticipationSuccess || $[72] !== showParticipationAction) {
    t31 = showParticipationAction && <div className="bg-gray-50 p-4 rounded-xl border border-gray-100"><InitiativeParticipationAction initiative={initiative} onSuccess={onParticipationSuccess} /></div>;
    $[70] = initiative;
    $[71] = onParticipationSuccess;
    $[72] = showParticipationAction;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== attendanceSettings || $[75] !== initiative.engagement?.attendance?.registered?.count || $[76] !== initiative.id) {
    t32 = attendanceSettings.enabled && attendanceSettings.showRegistrationBlock !== false && (attendanceSettings.blockPosition || "sidebar") !== "main" && <RegistrationBlock objectId={String(initiative.id)} objectType="initiative" attendanceSettings={attendanceSettings} attendanceCount={initiative.engagement?.attendance?.registered?.count} />;
    $[74] = attendanceSettings;
    $[75] = initiative.engagement?.attendance?.registered?.count;
    $[76] = initiative.id;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== occasions) {
    t33 = <OccasionsSection occasions={occasions} />;
    $[78] = occasions;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== demographics || $[81] !== showDemographicsSummary) {
    t34 = showDemographicsSummary && <DemographicsSection demographics={demographics} />;
    $[80] = demographics;
    $[81] = showDemographicsSummary;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== initiative.data || $[84] !== showActivation) {
    t35 = showActivation && <ActivationDetailsSection settings={(initiative.data as any).activation} />;
    $[83] = initiative.data;
    $[84] = showActivation;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== initiative.tags) {
    t36 = initiative.tags || [];
    $[86] = initiative.tags;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== t36) {
    t37 = <TagsSection tags={t36} />;
    $[88] = t36;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  const t38 = (initiative.data as any)?.hubs;
  let t39;
  if ($[90] !== initiative.systemTags || $[91] !== t38) {
    t39 = <LinkedHubs systemTags={initiative.systemTags} cachedHubs={t38} />;
    $[90] = initiative.systemTags;
    $[91] = t38;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== t30 || $[94] !== t31 || $[95] !== t32 || $[96] !== t33 || $[97] !== t34 || $[98] !== t35 || $[99] !== t37 || $[100] !== t39) {
    t40 = <div className="space-y-8 order-2 lg:order-2 lg:row-span-2">{t30}{t31}{t32}{t33}{t34}{t35}{t37}{t39}</div>;
    $[93] = t30;
    $[94] = t31;
    $[95] = t32;
    $[96] = t33;
    $[97] = t34;
    $[98] = t35;
    $[99] = t37;
    $[100] = t39;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== canEdit || $[103] !== components) {
    t41 = <ComponentsSection components={components} canEdit={canEdit} />;
    $[102] = canEdit;
    $[103] = components;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== addressDetail || $[106] !== displayAddress || $[107] !== hasMapCoords || $[108] !== shouldShowAddress || $[109] !== shouldShowMap) {
    t42 = <LocationSection addressDetail={addressDetail} displayAddress={displayAddress} hasMapCoords={hasMapCoords} shouldShowAddress={shouldShowAddress} shouldShowMap={shouldShowMap} />;
    $[105] = addressDetail;
    $[106] = displayAddress;
    $[107] = hasMapCoords;
    $[108] = shouldShowAddress;
    $[109] = shouldShowMap;
    $[110] = t42;
  } else {
    t42 = $[110];
  }
  let t43;
  if ($[111] !== t41 || $[112] !== t42) {
    t43 = <div className="space-y-10 order-3 lg:order-3 min-w-0">{t41}{t42}</div>;
    $[111] = t41;
    $[112] = t42;
    $[113] = t43;
  } else {
    t43 = $[113];
  }
  let t44;
  if ($[114] !== t29 || $[115] !== t40 || $[116] !== t43) {
    t44 = <div className="px-4 pt-2 pb-4 md:px-6 md:pb-6"><div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">{t29}{t40}{t43}</div></div>;
    $[114] = t29;
    $[115] = t40;
    $[116] = t43;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== galleryDisplayMode || $[119] !== galleryItems) {
    t45 = <GallerySection galleryItems={galleryItems} displayMode={galleryDisplayMode} />;
    $[118] = galleryDisplayMode;
    $[119] = galleryItems;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  let t46;
  if ($[121] !== initiative.id || $[122] !== relatedContentConfig) {
    t46 = relatedContentConfig?.enabled && relatedContentConfig.tags && relatedContentConfig.tags.length > 0 && <RelatedContentSection objectType="initiative" objectId={initiative.id} tags={relatedContentConfig.tags} title={relatedContentConfig.title} blurb={relatedContentConfig.blurb} />;
    $[121] = initiative.id;
    $[122] = relatedContentConfig;
    $[123] = t46;
  } else {
    t46 = $[123];
  }
  let t47;
  if ($[124] !== contactDetails || $[125] !== shouldShowContactDetails) {
    t47 = shouldShowContactDetails && <div className="p-4 md:p-6 border-t border-gray-100"><ContactInfo contactDetails={contactDetails} /></div>;
    $[124] = contactDetails;
    $[125] = shouldShowContactDetails;
    $[126] = t47;
  } else {
    t47 = $[126];
  }
  let t48;
  if ($[127] !== activeEnquiryType || $[128] !== initiative.id || $[129] !== showEnquiry) {
    t48 = showEnquiry && <div className="p-4 md:p-6 border-t border-gray-100"><EnquirySection key={activeEnquiryType ?? "default"} ref={enquiryFormRef} initiativeId={initiative.id} enquiryTypes={enquiryTypes} defaultEnquiryType={activeEnquiryType} /></div>;
    $[127] = activeEnquiryType;
    $[128] = initiative.id;
    $[129] = showEnquiry;
    $[130] = t48;
  } else {
    t48 = $[130];
  }
  let t49;
  if ($[131] !== allowComments || $[132] !== initiative.id) {
    t49 = allowComments && <div className="p-4 md:p-6 border-t border-gray-100"><CommentSection objectId={String(initiative.id)} objectType="initiative" canComment={allowComments} /></div>;
    $[131] = allowComments;
    $[132] = initiative.id;
    $[133] = t49;
  } else {
    t49 = $[133];
  }
  let t50;
  if ($[134] !== canManageMembers || $[135] !== initiative.id) {
    t50 = canManageMembers && <div className="p-4 md:p-6 border-t border-gray-100"><h3 className="text-lg font-semibold text-gray-900 mb-4">Assignments</h3><AssignmentTableBulkActions initiativeId={initiative.id} canManage={canManageMembers} /></div>;
    $[134] = canManageMembers;
    $[135] = initiative.id;
    $[136] = t50;
  } else {
    t50 = $[136];
  }
  let t51;
  if ($[137] !== canViewAnalytics || $[138] !== initiative) {
    t51 = canViewAnalytics && <AnalyticsSection initiative={initiative} />;
    $[137] = canViewAnalytics;
    $[138] = initiative;
    $[139] = t51;
  } else {
    t51 = $[139];
  }
  let t52;
  if ($[140] !== initiative || $[141] !== isAdmin || $[142] !== isCloneDialogOpen) {
    t52 = isAdmin && <NewInitiativeDialog isOpen={isCloneDialogOpen} onClose={() => setIsCloneDialogOpen(false)} cloneFrom={initiative} ownerId={initiative.ownerId} ownerType={initiative.ownerType} title={`Clone: ${initiative.name}`} />;
    $[140] = initiative;
    $[141] = isAdmin;
    $[142] = isCloneDialogOpen;
    $[143] = t52;
  } else {
    t52 = $[143];
  }
  let t53;
  if ($[144] !== t22 || $[145] !== t23 || $[146] !== t44 || $[147] !== t45 || $[148] !== t46 || $[149] !== t47 || $[150] !== t48 || $[151] !== t49 || $[152] !== t50 || $[153] !== t51 || $[154] !== t52) {
    t53 = <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-6xl mx-auto relative">{t22}{t23}{t44}{t45}{t46}{t47}{t48}{t49}{t50}{t51}{t52}</div>;
    $[144] = t22;
    $[145] = t23;
    $[146] = t44;
    $[147] = t45;
    $[148] = t46;
    $[149] = t47;
    $[150] = t48;
    $[151] = t49;
    $[152] = t50;
    $[153] = t51;
    $[154] = t52;
    $[155] = t53;
  } else {
    t53 = $[155];
  }
  return t53;
}
function _temp4(url) {
  return {
    original: url,
    thumbnail: url
  };
}
function _temp3() {}